/**
 * The status vocabulary, and how component statuses combine into one.
 *
 * Four levels, not three. `unknown` exists because the dashboard is a separate
 * process from the bot: when the bot is unreachable, the dashboard genuinely
 * does not know whether the Discord gateway is connected, and saying
 * "operational" would be a lie while saying "outage" would be a different one.
 *
 * Nothing in this file touches a database, a clock, or the network, so the
 * aggregation rule is testable on its own and is tested at every boundary.
 */

export const STATUS_LEVELS = ['operational', 'degraded', 'outage', 'unknown'] as const;
export type StatusLevel = (typeof STATUS_LEVELS)[number];

export const COMPONENT_IDS = [
  'dashboard_api',
  'postgres',
  'bot',
  'discord_gateway',
  'hermes',
  'jobs',
  'reconciliation',
  'derivation',
] as const;
export type ComponentId = (typeof COMPONENT_IDS)[number];

export interface ComponentStatus {
  id: ComponentId;
  status: StatusLevel;
  /** Round-trip time of the probe, when the probe had one. */
  latencyMs?: number;
  /**
   * Why, as a stable code from a closed set. Never a provider error string, a
   * connection URL, or anything a Discord member wrote.
   */
  reason?: string;
}

/**
 * Severity order, and the one judgement call in this file.
 *
 * `unknown` sits above `operational` and below `degraded`. Above, because an
 * unobserved component must never let the overall status read "everything is
 * fine" — that is the failure mode this whole page exists to avoid. Below,
 * because a component that is *known* to be misbehaving is a worse thing to be
 * told about than one nobody could reach, and burying a real degradation under
 * an unknown would be its own kind of dishonesty.
 */
const SEVERITY: Readonly<Record<StatusLevel, number>> = {
  operational: 0,
  unknown: 1,
  degraded: 2,
  outage: 3,
};

export function statusSeverity(level: StatusLevel): number {
  return SEVERITY[level];
}

/** The worse of two levels. */
export function worseStatus(a: StatusLevel, b: StatusLevel): StatusLevel {
  return SEVERITY[a] >= SEVERITY[b] ? a : b;
}

/**
 * Overall status: the worst component state.
 *
 * An empty component list is `unknown`, not `operational`. "We sampled nothing"
 * is not a clean bill of health.
 */
export function aggregateStatus(components: readonly ComponentStatus[]): StatusLevel {
  if (components.length === 0) return 'unknown';
  return components.reduce<StatusLevel>(
    (worst, component) => worseStatus(worst, component.status),
    'operational',
  );
}

/** Human labels. Text, always, so status never depends on colour alone. */
export const STATUS_LABELS: Readonly<Record<StatusLevel, string>> = {
  operational: 'Operational',
  degraded: 'Degraded',
  outage: 'Outage',
  unknown: 'Unknown',
};

export const COMPONENT_LABELS: Readonly<Record<ComponentId, string>> = {
  dashboard_api: 'Dashboard API',
  postgres: 'PostgreSQL',
  bot: 'Bot process',
  discord_gateway: 'Discord gateway',
  hermes: 'Hermes agent',
  jobs: 'Background jobs',
  reconciliation: 'Reconciliation',
  derivation: 'Memory derivation',
};

export const COMPONENT_DESCRIPTIONS: Readonly<Record<ComponentId, string>> = {
  dashboard_api: 'This process. It answered, so it is up.',
  postgres: 'The system of record. Probed directly by the dashboard, not through the bot.',
  bot: 'The Discord client process, probed over its readiness endpoint.',
  discord_gateway:
    'The bot’s gateway connection. Unknown whenever the bot itself cannot be reached.',
  hermes: 'The agent sidecar that produces grounded answers. Reported by the bot, not probed here.',
  jobs: 'Derivation queue depth, dead letters, and how long the oldest pending job has waited.',
  reconciliation: 'Catch-up runs and per-channel cursor errors.',
  derivation: 'Summary and embedding coverage over the spans that need them.',
};

/**
 * Statuses a component can be in, in the order a legend should list them.
 * Exported so the accessible table alternative and the colour strip cannot
 * drift apart.
 */
export const STATUS_ORDER: readonly StatusLevel[] = [
  'operational',
  'degraded',
  'outage',
  'unknown',
];
